import { Button } from 'components/Button/Button';
import { ReturnToHomeButton } from 'components/common/ReturnToHomeButton/ReturnToHomeButton';
import { TextInput } from 'components/Inputs/TextInput/TextInput';
import { FunctionComponent } from 'react';
import { PLATFORM_LEGAL_CORPORATION_LABEL } from 'services/api/constants';
import { useIntlByKey } from 'services/i18n/hooks/useIntlByKey';

import { PartialSubscriptionRequestBody } from '../../../services/api/subscriptionRequest/types';
import {
  ButtonsContainer,
  FormContainer,
  FormRequiredFieldMessage,
  FormRowContainer,
} from './SubscriptionRequestForm.style';
import { SubscriptionRequestFormKeys } from './subscriptionRequestFormKeys';
import { useSubscriptionRequestFormik } from './useSubscriptionRequestFormik';

interface SubscriptionRequestFormConfig {
  key: SubscriptionRequestFormKeys;
  labelKey: string;
  labelKeyParameters?: {
    [key: string]: string;
  };
}

const subscriptionRequestFormConfig: SubscriptionRequestFormConfig[][] = [
  [
    { key: SubscriptionRequestFormKeys.LAST_NAME, labelKey: 'last-name' },
    { key: SubscriptionRequestFormKeys.FIRST_NAME, labelKey: 'first-name' },
  ],
  [
    { key: SubscriptionRequestFormKeys.COMPANY_NAME, labelKey: 'company-name' },
    { key: SubscriptionRequestFormKeys.COMPANY_JOB, labelKey: 'company-job' },
  ],
  [
    {
      key: SubscriptionRequestFormKeys.VISITED_SITE,
      labelKey: 'visited-site',
      labelKeyParameters: {
        legalCorporationLabel: PLATFORM_LEGAL_CORPORATION_LABEL,
      },
    },
  ],
  [{ key: SubscriptionRequestFormKeys.SIRET, labelKey: 'siret' }],
  [
    { key: SubscriptionRequestFormKeys.EMAIL, labelKey: 'email' },
    { key: SubscriptionRequestFormKeys.PHONE_NUMBER, labelKey: 'phone-number' },
  ],
];

export const SubscriptionRequestForm: FunctionComponent<{
  initialValues?: PartialSubscriptionRequestBody;
}> = ({ initialValues }) => {
  const formik = useSubscriptionRequestFormik(initialValues);
  const subscriptionRequestFormIntl = useIntlByKey('subscription-request.form');

  return (
    <FormContainer onSubmit={formik.handleSubmit}>
      {subscriptionRequestFormConfig.map((formRowConfig) => (
        <FormRowContainer key={formRowConfig[0].key} $fullWidth={formRowConfig.length === 1}>
          {formRowConfig.map((formRowItemConfig) => {
            return (
              <TextInput
                key={formRowItemConfig.key}
                id={formRowItemConfig.key}
                name={formRowItemConfig.key}
                label={subscriptionRequestFormIntl(
                  `${formRowItemConfig.labelKey}.label`,
                  formRowItemConfig.labelKeyParameters,
                )}
                placeholder={subscriptionRequestFormIntl(
                  `${formRowItemConfig.labelKey}.placeholder`,
                )}
                value={formik.values[formRowItemConfig.key]}
                onChange={formik.handleChange}
                error={
                  formik.touched[formRowItemConfig.key] &&
                  Boolean(formik.errors[formRowItemConfig.key])
                }
                helperText={
                  formik.touched[formRowItemConfig.key] && formik.errors[formRowItemConfig.key]
                }
                required
              />
            );
          })}
        </FormRowContainer>
      ))}
      <FormRequiredFieldMessage>
        {subscriptionRequestFormIntl('required-field-message')}
      </FormRequiredFieldMessage>
      <ButtonsContainer>
        <Button
          displayStyle="filledOrange"
          type="submit"
          disabled={formik.isSubmitting}
          isLoading={formik.isSubmitting}
          bigPaddings
          fullWidth
        >
          {subscriptionRequestFormIntl('submit-subscription-request')}
        </Button>
        <ReturnToHomeButton displayStyle="underlineText" fullWidth />
      </ButtonsContainer>
    </FormContainer>
  );
};
